import { PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, Checkbox, Button, Typography } from 'antd';
import { useState } from 'react';

import {
  AddIntegrationModal,
  SortableIntegrationItem,
} from '@app/pages/Notifications/components/RecordsPriorityIntegration/components';
import type { RecordsPriorityIntegrationProps } from '@app/pages/Notifications/components/RecordsPriorityIntegration/types';

import type { DragEndEvent } from '@dnd-kit/core';
import type { CheckboxChangeEvent } from 'antd';

const { Text } = Typography;

export const RecordsPriorityIntegration: React.FC<
  RecordsPriorityIntegrationProps
> = ({
  defaultIntegrations,
  setDefaultIntegrations,
  data,
  sendToAll,
  setSendToAll,
  sendByLastActiveIntegration,
  setSendByLastActiveIntegration,
  disabled = false,
}) => {
  const [isAddingModalOpen, setIsAddingModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDefaultIntegrations((items) => {
        const oldIndex = items.findIndex((item) => item.Id === active.id);
        const newIndex = items.findIndex((item) => item.Id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        return newItems.map((item, index) => ({
          ...item,
          Order: index,
        }));
      });
    }
  };

  const handleSendToAllChange = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setSendToAll(checked);
  };

  const handleSendByLastActiveIntegrationChange = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setSendByLastActiveIntegration(checked);
    if (checked) {
      setDefaultIntegrations([]);
    }
  };

  const handleAddIntegration = () => {
    setIsAddingModalOpen(true);
  };

  return (
    <Card
      title="Интеграции для отправки"
      style={{ width: '100%', maxWidth: 600 }}
    >
      <div style={{ marginTop: 16 }}>
        <Checkbox
          checked={sendByLastActiveIntegration}
          onChange={handleSendByLastActiveIntegrationChange}
          style={{ marginBottom: 16, fontSize: '14px' }}
          disabled={disabled}
        >
          Отправлять в последнюю активную интеграцию диалога с клиентом
        </Checkbox>
        <Checkbox
          checked={sendToAll}
          onChange={handleSendToAllChange}
          style={{ marginBottom: 16, fontSize: '14px' }}
          disabled={disabled}
        >
          Отправлять во все выбранные интеграции
        </Checkbox>

        {sendByLastActiveIntegration && (
          <Text type="secondary">
            <br />
            Сообщение отправится с последней интеграции, с которой клиент
            взаимодействовал.
            <br />
          </Text>
        )}

        {!sendByLastActiveIntegration && (
          <>
            <Text type="secondary">
              <br />
              Выберите интеграции с которых необходимо отправлять уведомления.
              <br />
              Сообщение отправляется в первую доступную интеграцию в порядке
              приоритета.
            </Text>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={defaultIntegrations.map((item) => item.Id)}
                strategy={verticalListSortingStrategy}
                disabled={disabled}
              >
                <div style={{ marginBottom: 16 }}>
                  {defaultIntegrations.length > 0 ? (
                    defaultIntegrations.map((integration) => (
                      <SortableIntegrationItem
                        key={integration.Id}
                        integration={integration}
                        data={data}
                        setDefaultIntegrations={setDefaultIntegrations}
                      />
                    ))
                  ) : (
                    <div
                      style={{
                        padding: '20px',
                        textAlign: 'center',
                        border: '1px dashed #d9d9d9',
                        borderRadius: '6px',
                        marginBottom: '16px',
                      }}
                    >
                      <Text type="secondary">Нет добавленных интеграций</Text>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddIntegration}
              style={{ width: '100%' }}
              disabled={disabled}
            >
              Добавить социальную интеграцию
            </Button>
          </>
        )}
      </div>
      <AddIntegrationModal
        isOpen={isAddingModalOpen}
        setIsOpen={setIsAddingModalOpen}
        defaultIntegrations={defaultIntegrations}
        data={data}
        setDefaultIntegrations={setDefaultIntegrations}
      />
    </Card>
  );
};
